import { DateTime, Interval } from 'luxon';

import { DashboardUsageReportItemInfoFragment, DashboardWorkLetterUsageItem } from 'src/api';

export type DemoDashboardScale = 'employer' | 'peo';

type DemoVerificationType =
  | 'Verification_Of_Employment'
  | 'Verification_Of_Income'
  | 'Governmental'
  | 'Reverification';

type DemoWorkLetterType = 'Employment' | 'Employment_With_Income_Info' | 'Immigration';

const DEMO_VERIFICATION_TYPES: DemoVerificationType[] = [
  'Verification_Of_Employment',
  'Verification_Of_Income',
  'Governmental',
  'Reverification',
];

const DEMO_WORK_LETTER_TYPES: DemoWorkLetterType[] = [
  'Employment',
  'Employment_With_Income_Info',
  'Immigration',
];

const DEMO_DASHBOARD_REPORT_DATA: Record<
  DemoDashboardScale,
  {
    activeRecordAmount: number;
    inactiveRecordAmount: number;
    activeEmployeeUserAmount: number;
    inactiveEmployeeUserAmount: number;
    resolvedDataDisputeAmount: number;
    pendingDataDisputeAmount: number;
  }
> = {
  employer: {
    activeRecordAmount: 19_847,
    inactiveRecordAmount: 41_293,
    activeEmployeeUserAmount: 8_142,
    inactiveEmployeeUserAmount: 11_876,
    resolvedDataDisputeAmount: 6,
    pendingDataDisputeAmount: 1,
  },
  peo: {
    activeRecordAmount: 79_412,
    inactiveRecordAmount: 171_638,
    activeEmployeeUserAmount: 34_891,
    inactiveEmployeeUserAmount: 54_227,
    resolvedDataDisputeAmount: 27,
    pendingDataDisputeAmount: 2,
  },
};

/** Hand-crafted monthly counts per report type — types grow at different rates with occasional dips. */
const MONTHLY_TYPE_COUNTS: Record<DemoDashboardScale, Record<DemoVerificationType, number[]>> = {
  employer: {
    Verification_Of_Employment: [42, 49, 56, 67, 74, 81, 86, 91, 94],
    Verification_Of_Income: [25, 30, 28, 36, 41, 47, 51, 48, 55],
    Governmental: [12, 13, 12, 16, 19, 21, 22, 24, 23],
    Reverification: [4, 5, 7, 8, 7, 6, 8, 9, 7],
  },
  peo: {
    Verification_Of_Employment: [208, 244, 278, 331, 368, 403, 427, 451, 468],
    Verification_Of_Income: [124, 147, 139, 176, 203, 231, 253, 241, 269],
    Governmental: [58, 64, 61, 79, 93, 105, 110, 117, 112],
    Reverification: [19, 23, 32, 37, 33, 28, 39, 41, 36],
  },
};

/** Hand-crafted monthly work letter counts per type — immigration letters stay the rarest. */
const MONTHLY_WORK_LETTER_COUNTS: Record<
  DemoDashboardScale,
  Record<DemoWorkLetterType, number[]>
> = {
  employer: {
    Employment: [16, 21, 19, 26, 31, 35, 38, 41, 44],
    Employment_With_Income_Info: [9, 12, 14, 13, 18, 21, 24, 23, 27],
    Immigration: [3, 4, 3, 6, 5, 7, 8, 7, 9],
  },
  peo: {
    Employment: [78, 102, 94, 127, 151, 172, 187, 203, 218],
    Employment_With_Income_Info: [44, 58, 67, 63, 87, 103, 118, 112, 133],
    Immigration: [14, 19, 16, 28, 24, 33, 38, 34, 43],
  },
};

export const getDemoDashboardReportData = (scale: DemoDashboardScale) => {
  return DEMO_DASHBOARD_REPORT_DATA[scale];
};

const deterministicWobble = (
  monthIndex: number,
  type: DemoVerificationType | DemoWorkLetterType,
) => {
  const seed = monthIndex * 17 + type.length * 3;
  return (seed % 5) - 2;
};

/** Continues a hand-crafted series past its last month, keeping its recent slope. */
const projectBeyondSeries = (
  series: readonly number[],
  monthIndex: number,
  growthFactor: number,
) => {
  const lastIndex = series.length - 1;
  const lastValue = series[lastIndex] ?? 1;
  const priorValue = series[Math.max(0, lastIndex - 2)] ?? lastValue;
  const averageMonthlyDelta = Math.round((lastValue - priorValue) / 2);
  const monthsBeyondSeries = monthIndex - lastIndex;

  return (
    lastValue + monthsBeyondSeries * Math.max(1, Math.round(averageMonthlyDelta * growthFactor))
  );
};

const extrapolateTypeCount = (
  series: readonly number[],
  monthIndex: number,
  type: DemoVerificationType,
) => {
  const typeGrowthFactor =
    type === 'Verification_Of_Employment'
      ? 1.1
      : type === 'Verification_Of_Income'
        ? 0.85
        : type === 'Governmental'
          ? 0.55
          : 0.35;

  const projected = projectBeyondSeries(series, monthIndex, typeGrowthFactor);

  const occasionalDip = type !== 'Verification_Of_Employment' && monthIndex % 4 === 2 ? -2 : 0;

  return Math.max(1, projected + deterministicWobble(monthIndex, type) + occasionalDip);
};

const getTypeCountForMonth = (
  scale: DemoDashboardScale,
  type: DemoVerificationType,
  monthIndex: number,
) => {
  const series = MONTHLY_TYPE_COUNTS[scale][type];

  if (monthIndex < series.length) {
    return series[monthIndex] ?? 1;
  }

  return extrapolateTypeCount(series, monthIndex, type);
};

export const getDemoDashboardUsageReports = ({
  scale,
  from,
  to,
}: {
  scale: DemoDashboardScale;
  from: DateTime;
  to: DateTime;
}): DashboardUsageReportItemInfoFragment[] => {
  const interval = Interval.fromDateTimes(from.startOf('month'), to.endOf('month'));
  const months = interval.splitBy({ months: 1 }).map(monthInterval => monthInterval.start);

  return months.flatMap((monthStart, index) => {
    if (!monthStart?.isValid) return [];

    const monthLabel = monthStart.toFormat('MMMM yyyy');

    return DEMO_VERIFICATION_TYPES.map(type => ({
      __typename: 'DashboardUsageReportItem' as const,
      month: monthLabel,
      type,
      count: String(getTypeCountForMonth(scale, type, index)),
    })).filter(item => Number(item.count) > 0);
  });
};

const WORK_LETTER_TYPE_GROWTH_FACTORS: Record<DemoWorkLetterType, number> = {
  Employment: 1.05,
  Employment_With_Income_Info: 0.8,
  Immigration: 0.4,
};

const getWorkLetterCountForMonth = (
  scale: DemoDashboardScale,
  type: DemoWorkLetterType,
  monthIndex: number,
) => {
  const series = MONTHLY_WORK_LETTER_COUNTS[scale][type];

  if (monthIndex < series.length) {
    return series[monthIndex] ?? 1;
  }

  const projected = projectBeyondSeries(series, monthIndex, WORK_LETTER_TYPE_GROWTH_FACTORS[type]);

  return Math.max(1, projected + deterministicWobble(monthIndex, type));
};

export const getDemoDashboardWorkLetterUsage = ({
  scale,
  from,
  to,
}: {
  scale: DemoDashboardScale;
  from: DateTime;
  to: DateTime;
}): DashboardWorkLetterUsageItem[] => {
  const interval = Interval.fromDateTimes(from.startOf('month'), to.endOf('month'));
  const months = interval.splitBy({ months: 1 }).map(monthInterval => monthInterval.start);

  return months.flatMap((monthStart, index) => {
    if (!monthStart?.isValid) return [];

    const monthLabel = monthStart.toFormat('MMMM yyyy');

    return DEMO_WORK_LETTER_TYPES.map(type => ({
      __typename: 'DashboardWorkLetterUsageItem' as const,
      month: monthLabel,
      type,
      count: String(getWorkLetterCountForMonth(scale, type, index)),
    })).filter(item => Number(item.count) > 0);
  });
};
